import { Heading, PathShape, TileKind } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Cell, headingBetween } from "./cell";
import { CellKind, Grid } from "./grid";
import { SegmentPlacement, enumeratePlacements } from "./segment";

export enum Occupant { None = 0, Designed = 1, Tower = 3 }

/**
 * One rung of the keep-extending rule (§4.3), strictest first. `room`: the road end keeps the open ground — a
 * placement (or a tower) may cut off a small pocket, or split the ground and take the larger part. `horizon`: some run of the shapes the
 * road may still be offered fits from the new open end for at least that many more cells, every bridge span landed
 * (0: only that the road does not end mid-span).
 */
interface ExtendRung { readonly room: boolean; readonly horizon: number }

/**
 * The level's road: segments appended outward from the base. The enemy portal is the road's open end and
 * moves as the road grows; enemies walk the road back to the base. Towers may only stand next to the road.
 * The road never finishes: what bounds its length is how often road cards are offered (the road pace, §4.4).
 * Owns every placement rule from DESIGN.md §4.3 and §4.6.
 */
export class RoadMap {
  /** Longest run of chasm one bridge may span: the art kit's plank spans are one, two and three cells. */
  static readonly MaxBridge = 3;
  /** Nodes one extension question may visit (§4.3) before it gives the placement the benefit of the doubt. */
  static readonly MaxSearchNodes = 4000;
  /** Cells of road the offerable shapes must still be able to add after any placement: about three pieces. */
  static readonly ExtendHorizon = 8;
  /** Ground (4-connected cells, flood fill) a placement may cut off behind the road end whatever is left ahead: the small pockets a coil leaves. */
  static readonly PocketCells = 4;
  /** Strictest first; the road keeps the strictest rung some shape can keep (the lower ones only on a crowded map). */
  static readonly Rungs: readonly ExtendRung[] = [
    { room: true, horizon: RoadMap.ExtendHorizon },
    { room: false, horizon: RoadMap.ExtendHorizon },
    { room: false, horizon: 1 },
    { room: false, horizon: 0 },
  ];
  /** Cells of road history a crossing check needs before the fresh cells: a full bridge span, its bank and the step onto it. */
  private static readonly TailCells = RoadMap.MaxBridge + 2;

  private readonly occupancy: Uint8Array;
  private readonly designed: Cell[] = [];
  private readonly designedKinds: TileKind[] = [];
  private readonly segmentShapes: number[] = [];
  /** Road pace laid so far (§4.4): the sum of the placed segments' `paceCost`, which a special tile may set apart from its cell count. */
  private paced = 0;
  private road: Cell[] = [];
  private roadKinds: TileKind[] = [];
  /** Shapes the player could still be offered (§4.3); none switches the keep-extending rule off. */
  private shapes: readonly PathShape[] = [];
  /** Bumped on every change a placement rule reads (road, towers, shapes): the caches below hold for one version. */
  private version = 0;
  private cachedVersion = -1;
  private readonly placementCache = new Map<number, SegmentPlacement[]>();
  private readonly segmentCache = new Map<string, boolean>();
  private readonly towerCache = new Map<number, boolean>();
  /** Search states (occupancy + open end + cells still needed) proven unable to extend, at this version. */
  private readonly deadEnds = new Set<string>();
  /** The strictest rung some placement keeps now (`Rungs.length`: none — the road has stopped); undefined until asked. */
  private rung: number | undefined = undefined;
  /** The first placement that keeps the rung, and its cells with the run found after it: a tower elsewhere cannot break it. */
  private witness = new Set<number>();
  private witnessPlacement: SegmentPlacement | null = null;
  /** Free ground reachable from the open end; undefined until asked (per version, and per tower tried). */
  private groundAhead: number | undefined = undefined;
  /** Per-question search state: nodes visited, the capped shapes' uses laid by the search itself, what is assumed. */
  private nodes = 0;
  private capHit = false;
  private assumed = "";
  private readonly searchUses: number[] = [];
  /** 1 where the grid lets road run (open ground or chasm), by cell index: the flood fill's terrain test without a Cell. */
  private readonly roadGround: Uint8Array;
  private readonly floodMark: Uint32Array;
  private floodStamp = 0;
  private readonly floodStack: Int32Array;
  /** Diagnostics for the sweeps and tests (never hashed): questions asked, nodes visited, questions capped, rungs relaxed. */
  readonly searchStats = { questions: 0, nodes: 0, capped: 0, relaxed: 0 };

  constructor(readonly grid: Grid) {
    this.occupancy = new Uint8Array(grid.cellCount);
    this.floodMark = new Uint32Array(grid.cellCount);
    this.floodStack = new Int32Array(grid.cellCount);
    this.roadGround = new Uint8Array(grid.cellCount);
    for (let i = 0; i < grid.cellCount; i++) {
      const kind = grid.kindAt(grid.cellAt(i));
      this.roadGround[i] = kind === CellKind.Empty || kind === CellKind.Chasm ? 1 : 0;
    }
    this.recomputeRoad();
  }

  /** Set by the sim to the unlocked shapes: what the road may still be continued with (§4.3). */
  get continuationShapes(): readonly PathShape[] { return this.shapes; }
  set continuationShapes(shapes: readonly PathShape[]) { this.shapes = shapes; this.version++; }

  get designedTileCount(): number { return this.designed.length; }
  /** Road pace laid (§4.4): what the road-card interval is read at. Its cell count except where a special tile trades on it. */
  get paceLaid(): number { return this.paced; }
  get designedCells(): readonly Cell[] { return this.designed; }
  /** Walking order for enemies: portal (open end) … first tile, base. */
  get roadCells(): readonly Cell[] { return this.road; }
  get roadTileKinds(): readonly TileKind[] { return this.roadKinds; }
  get segmentCount(): number { return this.segmentShapes.length; }
  /** Where the next segment attaches and where enemies appear. The base itself when nothing is built. */
  get openEnd(): Cell { return this.designed.length === 0 ? this.grid.base : this.designed[this.designed.length - 1]; }
  get portal(): Cell { return this.openEnd; }
  get atBase(): boolean { return this.designed.length === 0; }
  /** Road length in edges from the portal to the base. */
  get length(): Fix64 { return Fix64.fromInt(this.designed.length); }

  get heading(): Heading {
    if (this.designed.length === 0) return this.grid.baseHeading;
    const prev = this.designed.length === 1 ? this.grid.base : this.designed[this.designed.length - 2];
    return headingBetween(prev, this.designed[this.designed.length - 1]);
  }

  occupantAt(c: Cell): Occupant { return this.occupancy[this.grid.index(c)] as Occupant; }

  countSegmentsOfShape(shapeIndex: number): number {
    let n = 0;
    for (const s of this.segmentShapes) if (s === shapeIndex) n++;
    return n;
  }

  /** Road may be laid on open ground and across a chasm — the crossing is what a bridge is for. */
  isCellFreeForRoad(c: Cell): boolean {
    if (!this.grid.inBounds(c) || this.occupancy[this.grid.index(c)] !== Occupant.None) return false;
    const kind = this.grid.kindAt(c);
    return kind === CellKind.Empty || kind === CellKind.Chasm;
  }
  /** True where the road actually runs. */
  isRoad(c: Cell): boolean { return this.grid.inBounds(c) && this.occupancy[this.grid.index(c)] === Occupant.Designed; }

  /** Geometry only: attaches at the open end, never doubles back, uses free cells, crosses chasms straight. */
  private isSegmentGeometryValid(p: SegmentPlacement): boolean {
    if (p.cells.length === 0 || !this.openEnd.isAdjacent4(p.cells[0])) return false;
    if (this.atBase) {
      if (!p.cells[0].equals(this.grid.base.step(this.grid.baseHeading))) return false;
    } else {
      const previous = this.designed.length === 1 ? this.grid.base : this.designed[this.designed.length - 2];
      if (p.cells[0].equals(previous)) return false;
    }
    return this.fitsAfter(this.tail(), p);
  }

  /** The last few cells of the road from the base outward: enough history to judge a crossing. */
  private tail(): Cell[] { return [this.grid.base, ...this.designed].slice(-RoadMap.TailCells); }

  /** Free, distinct cells and a legal crossing for `g` laid after `tail`. */
  private fitsAfter(tail: readonly Cell[], g: SegmentPlacement): boolean {
    for (let i = 0; i < g.cells.length; i++) {
      if (!this.isCellFreeForRoad(g.cells[i])) return false;
      for (let j = 0; j < i; j++) if (g.cells[j].equals(g.cells[i])) return false;
    }
    return this.crossingsHold([...tail, ...g.cells], g.cells.length);
  }

  /**
   * A chasm is crossed by a bridge, and a bridge runs bank to bank: the road enters the chasm from ground, goes
   * straight across at most `MaxBridge` cells and lands on ground straight ahead. No turning mid-span (a bridge
   * cannot bend), no running along a gorge. Checks the last `fresh` cells of `tail` (and the open end they attach to).
   * A span still open at the end is legal here: the keep-extending rule is what makes sure it lands.
   */
  private crossingsHold(tail: readonly Cell[], fresh: number): boolean {
    let run = 0;
    const firstChecked = Math.max(1, tail.length - fresh - 1);
    for (let k = 0; k < tail.length; k++) {
      run = this.grid.kindAt(tail[k]) === CellKind.Chasm ? run + 1 : 0;
      if (k < firstChecked || run === 0) continue;
      if (run > RoadMap.MaxBridge) return false;
      if (k + 1 < tail.length && headingBetween(tail[k - 1], tail[k]) !== headingBetween(tail[k], tail[k + 1])) return false;
    }
    return true;
  }

  /**
   * Geometry plus the keep-extending rule (§4.3): the road never finishes, so no placement may box its open end in.
   * Afterwards the road end must keep the open ground (it may leave a pocket of at most `PocketCells` behind, and where
   * it splits the ground in two it must go on into the larger part), and some run of the shapes it may still be offered (unlocked, under their
   * per-level caps) must fit from the new open end for `ExtendHorizon` more cells, every bridge span landed. On a map
   * the road has nearly filled no placement may manage that, and the rule steps down a rung at a time (`Rungs`) — the
   * horizon without the room, then one more piece, then only no bridge to nowhere — so the road still grows as far as
   * it can; once nothing fits at all it has stopped and no road card is offered. The search is deterministic and
   * bounded (`MaxSearchNodes`), and its answers are cached until the road or a tower changes.
   */
  canPlaceSegment(p: SegmentPlacement): boolean {
    if (!this.isSegmentGeometryValid(p)) return false;
    if (this.shapes.length === 0) return true;
    const rung = Math.min(this.extendRung, RoadMap.Rungs.length - 1);
    const key = `${p.shapeIndex}:${p.cells.map((c) => this.grid.index(c)).join(",")}`;
    let ok = this.segmentCache.get(key);
    if (ok === undefined) {
      ok = this.keeps(p, rung, null);
      this.segmentCache.set(key, ok);
    }
    return ok;
  }

  /** The strictest rung (index into `Rungs`) some offerable shape keeps from here; `Rungs.length` once none fits at all. */
  get extendRung(): number {
    this.refreshCaches();
    if (this.rung === undefined) {
      this.rung = this.firstRungKept(null);
      if (this.rung > 0) this.searchStats.relaxed++;
    }
    return this.rung;
  }

  /** Could the road still be extended right now (ignoring which cards are in hand)? */
  get canStillExtend(): boolean { return this.shapes.length === 0 || this.extendRung < RoadMap.Rungs.length; }

  /**
   * The strictest rung at or after `from` (0 when null) that some placement of an offerable shape keeps, in the fixed
   * shape and orientation order; notes the first such placement's cells (and its run's) as the witness.
   */
  private firstRungKept(only: number | null): number {
    const first = only ?? 0, last = only ?? RoadMap.Rungs.length - 1;
    for (let r = first; r <= last; r++) {
      for (const shape of this.shapes) {
        if (!this.hasUsesLeft(shape)) continue;
        for (const g of enumeratePlacements(shape, this.openEnd, this.heading, this.atBase)) {
          if (!this.isSegmentGeometryValid(g)) continue;
          const cells = new Set<number>();
          if (!this.keeps(g, r, cells)) continue;
          if (only === null) { this.witness = cells; this.witnessPlacement = g; }
          return r;
        }
      }
    }
    return RoadMap.Rungs.length;
  }

  /** Whether placing `p` (valid geometry) keeps rung `r`; `out` collects its cells and the run found after it. */
  private keeps(p: SegmentPlacement, r: number, out: Set<number> | null): boolean {
    const rung = RoadMap.Rungs[r];
    const before = rung.room ? this.groundFromOpenEnd() : 0;
    const added: number[] = [];
    for (const c of p.cells) { const i = this.grid.index(c); this.occupancy[i] = Occupant.Designed; added.push(i); }
    this.searchUses[p.shapeIndex] = (this.searchUses[p.shapeIndex] ?? 0) + 1;
    const n = p.cells.length, end = p.cells[n - 1];
    let ok = !rung.room || this.roomKept(before, n, this.flood(end, Number.POSITIVE_INFINITY));
    if (ok) {
      const heading = headingBetween(n >= 2 ? p.cells[n - 2] : this.openEnd, end);
      ok = this.question([...this.tail(), ...p.cells].slice(-RoadMap.TailCells), heading, rung.horizon, rung.room, added, out);
    }
    this.searchUses[p.shapeIndex]--;
    for (const i of added) this.occupancy[i] = Occupant.None;
    if (ok && out !== null) for (const i of added) out.add(i);
    return ok;
  }

  /** The room part of rung 0: what a placement of `laid` cells cut off behind the road end is a small pocket, or the smaller part. */
  private roomKept(before: number, laid: number, after: number): boolean {
    const cutOff = before - laid - after;
    return cutOff <= RoadMap.PocketCells || after >= cutOff;
  }

  private groundFromOpenEnd(): number {
    if (this.groundAhead === undefined) this.groundAhead = this.flood(this.openEnd, Number.POSITIVE_INFINITY);
    return this.groundAhead;
  }

  /** Drops the cached answers when the road, a tower or the shape list changed since they were worked out. */
  private refreshCaches(): void {
    if (this.cachedVersion === this.version) return;
    this.cachedVersion = this.version;
    this.placementCache.clear();
    this.segmentCache.clear();
    this.towerCache.clear();
    this.deadEnds.clear();
    this.rung = undefined;
    this.witness = new Set();
    this.witnessPlacement = null;
    this.groundAhead = undefined;
  }

  /**
   * Can the road, as the occupancy stands now, add at least `need` more cells from the open end `tail` ends on, and end
   * off a chasm — with every piece of that run keeping the room rule too when `room` (so the next placement can keep it
   * as well: a piece onto a bridge or into a corner that only leads on into a pocket does not count)? `added` lists the
   * cells laid since the committed road (part of the state's identity for the dead-end memo, with `assumed` naming
   * anything else, such as a tower); `out` collects one solution's cells.
   */
  private question(tail: readonly Cell[], heading: Heading, need: number, room: boolean, added: number[], out: Set<number> | null): boolean {
    this.nodes = 0;
    this.capHit = false;
    const ok = this.search(tail, heading, need, room, added, out);
    this.searchStats.questions++;
    this.searchStats.nodes += this.nodes;
    if (this.capHit) this.searchStats.capped++;
    return ok;
  }

  /** Depth-first, in the fixed shape and orientation order, so every answer is deterministic. */
  private search(tail: readonly Cell[], heading: Heading, need: number, room: boolean, added: number[], out: Set<number> | null): boolean {
    const openEnd = tail[tail.length - 1];
    if (need <= 0 && this.grid.kindAt(openEnd) !== CellKind.Chasm) return true;
    // Past the bound the answer is "yes": a slow question must not refuse a placement that may well be fine.
    if (++this.nodes > RoadMap.MaxSearchNodes) { this.capHit = true; return true; }
    const left = Math.max(need, 1);
    const key = this.stateKey(tail, left, room, added);
    if (this.deadEnds.has(key)) return false;
    const ground = this.flood(openEnd, room ? Number.POSITIVE_INFINITY : left);
    if (ground < left) { this.deadEnds.add(key); return false; }
    for (const shape of this.shapes) {
      if (!this.hasUsesLeft(shape)) continue;
      for (const g of enumeratePlacements(shape, openEnd, heading, false)) {
        if (!this.fitsAfter(tail, g)) continue;
        for (const c of g.cells) { const i = this.grid.index(c); this.occupancy[i] = Occupant.Designed; added.push(i); }
        this.searchUses[shape.index] = (this.searchUses[shape.index] ?? 0) + 1;
        const roomy = !room || this.roomKept(ground, g.cells.length, this.flood(g.cells[g.cells.length - 1], Number.POSITIVE_INFINITY));
        const ok = roomy && this.search([...tail, ...g.cells].slice(-RoadMap.TailCells), g.exitHeading, need - g.cells.length, room, added, out);
        this.searchUses[shape.index]--;
        for (let k = 0; k < g.cells.length; k++) this.occupancy[added.pop()!] = Occupant.None;
        if (ok) {
          if (out !== null) for (const c of g.cells) out.add(this.grid.index(c));
          return true;
        }
      }
    }
    // Every child answered no without reaching the bound (one that reached it answers yes), so this is a proven dead end.
    this.deadEnds.add(key);
    return false;
  }

  private stateKey(tail: readonly Cell[], need: number, room: boolean, added: readonly number[]): string {
    const capped = this.shapes.filter((s) => s.maxPerLevel > 0).map((s) => this.searchUses[s.index] ?? 0).join(".");
    return `${this.assumed}${room ? "r" : ""}|${need}|${tail.map((c) => this.grid.index(c)).join(".")}|${capped}|${[...added].sort((a, b) => a - b).join(".")}`;
  }

  private hasUsesLeft(shape: PathShape): boolean {
    return shape.maxPerLevel === 0 || this.countSegmentsOfShape(shape.index) + (this.searchUses[shape.index] ?? 0) < shape.maxPerLevel;
  }

  /** Free road cells 4-connected to `from` (not counting it), counted up to `cap`: the ground the road end can still reach. */
  private flood(from: Cell, cap: number): number {
    const w = this.grid.width, cells = this.occupancy.length, NONE = -1;
    const stamp = ++this.floodStamp;
    let top = 0, found = 0;
    this.floodMark[this.grid.index(from)] = stamp;
    this.floodStack[top++] = this.grid.index(from);
    while (top > 0) {
      const i = this.floodStack[--top];
      const x = i % w;
      for (let k = 0; k < 4; k++) {
        // north, south, east, west by index; a step off the grid's side is no neighbour
        const j = k === 0 ? i + w : k === 1 ? i - w : k === 2 ? (x + 1 < w ? i + 1 : NONE) : (x > 0 ? i - 1 : NONE);
        if (j < 0 || j >= cells || this.floodMark[j] === stamp || this.occupancy[j] !== Occupant.None || this.roadGround[j] === 0) continue;
        this.floodMark[j] = stamp;
        if (++found >= cap) return found;
        this.floodStack[top++] = j;
      }
    }
    return found;
  }

  /** Deterministic list of valid placements ("ghosts") for a shape at the current open end. */
  enumerateValidPlacements(shape: PathShape): SegmentPlacement[] {
    this.refreshCaches();
    let valid = this.placementCache.get(shape.index);
    if (valid === undefined) {
      valid = enumeratePlacements(shape, this.openEnd, this.heading, this.atBase).filter((p) => this.canPlaceSegment(p));
      this.placementCache.set(shape.index, valid);
    }
    return [...valid];
  }

  tryPlaceSegment(p: SegmentPlacement): boolean {
    if (!this.canPlaceSegment(p)) return false;
    for (const c of p.cells) {
      this.occupancy[this.grid.index(c)] = Occupant.Designed;
      this.designed.push(c);
      this.designedKinds.push(p.tileKind);
    }
    this.segmentShapes.push(p.shapeIndex);
    this.paced += p.paceCost;
    this.version++;
    this.recomputeRoad();
    return true;
  }

  /** True when any of the 8 neighbours is a road tile or the base (§4.6). */
  isNextToRoad(c: Cell): boolean {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const n = new Cell(c.x + dx, c.y + dy);
        if (!this.grid.inBounds(n)) continue;
        if (n.equals(this.grid.base) || this.occupancy[this.grid.index(n)] === Occupant.Designed) return true;
      }
    }
    return false;
  }

  /**
   * A tower needs ground next to the road and must not box the road in (§4.3): with it standing, some placement must
   * still keep the rung the road keeps now, and under the room rule the tower itself may cut off no more than a
   * placement could (a small pocket, or the smaller part). Off the witness (one placement known to keep the rung, and
   * its run) and with no room rule in force, a tower cannot change that; elsewhere it costs a search. A road already
   * stopped refuses no tower.
   */
  canPlaceTower(c: Cell): boolean {
    // a tower needs ground under it: open ground only, never a chasm the road happens to bridge
    if (this.grid.kindAt(c) !== CellKind.Empty) return false;
    if (!this.isCellFreeForRoad(c) || !this.isNextToRoad(c)) return false;
    if (this.shapes.length === 0) return true;
    const rung = this.extendRung;
    if (rung >= RoadMap.Rungs.length) return true;
    const i = this.grid.index(c);
    if (!RoadMap.Rungs[rung].room && !this.witness.has(i)) return true;
    let ok = this.towerCache.get(i);
    if (ok === undefined) {
      const ground = this.groundFromOpenEnd();
      this.occupancy[i] = Occupant.Tower;
      this.groundAhead = undefined;
      this.assumed = `t${i}`;
      // The witness first: it usually still keeps the rung, and the placements before it in the fixed order did not.
      ok = (!RoadMap.Rungs[rung].room || this.roomKept(ground, 1, this.groundFromOpenEnd()))
        && ((this.witnessPlacement !== null && this.isSegmentGeometryValid(this.witnessPlacement) && this.keeps(this.witnessPlacement, rung, null)) || this.firstRungKept(rung) === rung);
      this.assumed = "";
      this.groundAhead = ground;
      this.occupancy[i] = Occupant.None;
      this.towerCache.set(i, ok);
    }
    return ok;
  }

  tryPlaceTower(c: Cell): boolean {
    if (!this.canPlaceTower(c)) return false;
    this.occupancy[this.grid.index(c)] = Occupant.Tower;
    this.version++;
    return true;
  }

  removeTower(c: Cell): void {
    if (this.occupancy[this.grid.index(c)] !== Occupant.Tower) throw new Error(`No tower at ${c}`);
    this.occupancy[this.grid.index(c)] = Occupant.None;
    this.version++;
  }

  collectValidTowerCells(): Cell[] {
    const out: Cell[] = [];
    for (let i = 0; i < this.grid.cellCount; i++) {
      const c = this.grid.cellAt(i);
      if (this.canPlaceTower(c)) out.push(c);
    }
    return out;
  }

  private recomputeRoad(): void {
    this.road = [...this.designed].reverse();
    this.road.push(this.grid.base);
    this.roadKinds = [...this.designedKinds].reverse();
    this.roadKinds.push(TileKind.Normal);
  }
}
